"use client";

import React from "react";
import { Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const interestOptions = [{
  value: "cristiana",
  label: "Música Cristiana e Inspiradora"
}, {
  value: "personalizada",
  label: "Música Personalizada (Eventos)"
}, {
  value: "corporativa",
  label: "Proyectos Empresariales"
}, {
  value: "especial",
  label: "Proyectos Especiales"
}];
function InnerContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="mb-4 flex size-16 items-center justify-center rounded-lg bg-primary/20 text-primary">
          <Send className="size-8" />
        </div>
        <h3 className="mb-2 text-2xl font-semibold font-default text-foreground">¡Visión Enviada!</h3>
        <p className="mb-6 text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary/50 hover:bg-primary/10 text-primary">
          Enviar Otra Propuesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField control={control} name="name" rules={{
          required: "Por favor, ingrese su nombre",
          minLength: {
            value: 2,
            message: "El nombre debe tener al menos 2 caracteres"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/90 font-default">Nombre Completo</FormLabel>
                <FormControl>
                  <Input placeholder="Ej. Gabriel Rolver" {...field} className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" />
                </FormControl>
                <FormMessage className="text-destructive" />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "Por favor, ingrese su correo electrónico",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Por favor, ingrese un correo válido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/90 font-default">Correo Electrónico</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="contacto@ejemplo.com" {...field} className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" />
                </FormControl>
                <FormMessage className="text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="interest" rules={{
        required: "Por favor, seleccione el tipo de proyecto"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Tipo de Proyecto Musical</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all">
                    <SelectValue placeholder="Seleccione el estilo de su visión" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border/50">
                  {interestOptions.map((option, i) => <SelectItem key={option.value} value={option.value} data-index={i} className="focus:bg-primary/20">
                      {option.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Por favor, describa su visión",
        minLength: {
          value: 10,
          message: "La descripción debe tener al menos 10 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-default">Detalles de su Visión</FormLabel>
              <FormControl>
                <Textarea placeholder="Cuéntenos sobre su proyecto, el mensaje que desea transmitir y cualquier detalle musical..." className="min-h-[120px] resize-y bg-background/50 border-border/50 focus:border-primary/50 focus:ring-primary/20 transition-all" {...field} />
              </FormControl>
              <FormMessage className="text-destructive" />
            </FormItem>} />

        {submitError && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive border border-destructive/20">
            {submitError}
          </div>}

        <Button type="submit" className="w-full md:w-auto bg-gradient-to-r from-primary to-blue-700 hover:from-primary/90 hover:to-blue-800 text-primary-foreground font-default font-semibold transition-all duration-300" disabled={isSubmitting}>
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Procesando Visión...
            </> : <>
              <Send className="mr-2 size-4" />
              Enviar Visión
            </>}
        </Button>
      </div>
    </Form>;
}
export default function FormularioDeProyecto() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="249475" id="formulario-de-proyecto" className="relative w-full dark bg-background py-16 md:py-24 lg:py-32 overflow-hidden" ref={ref}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary/30 rounded-lg blur-[120px]" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary/20 rounded-lg blur-[120px]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className={`mx-auto max-w-3xl transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="mb-10 text-center">
            <h2 className="mb-4 text-3xl font-bold tracking-tight font-default text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
              Inicie su <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Proyecto Musical</span>
            </h2>
            <p className="mx-auto max-w-[600px] text-lg text-muted-foreground font-default">
              Transformamos su fe e inspiración en sonidos profesionales. Comparta su idea y nuestro equipo le contactará para darle vida.
            </p>
          </div>

          <Card className="border-border/30 bg-card/40 shadow-2xl backdrop-blur-xl rounded-2xl border-t-primary/20">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-default text-foreground">Detalles del Proyecto</CardTitle>
              <CardDescription className="text-muted-foreground">
                Complete los campos a continuación para comenzar el proceso creativo.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider formKey="project_form_contact" formId="project_form_contact" sectionName="Contact Form Section" defaultValues={{
              name: "",
              email: "",
              interest: "",
              message: ""
            }} submitText="Enviar Visión" successMessage="¡Gracias por confiar en Rolver Music Productions! Hemos recibido su propuesta y nos pondremos en contacto muy pronto.">
                <InnerContactForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}